import { EditorHandlers } from '@wix/editor-platform-host-integration-apis';
import { ResolverByExtensionType } from '../types';
import {
  ComponentsService,
  ManifestComponentExtensionType,
} from '@wix/editor-platform-manifest-services';

import {
  CheckboxItemType,
  CheckedStateType,
  ElementsPanelProps,
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-platform-feature-store';

import {
  BuilderStyle,
  CompRef,
  DocumentServicesObject,
} from '@wix/document-services-types';
import {
  CssPropertyItem,
  CssCustomProperties,
} from '@wix/component-protocol/schema';

const MAX_DEPTH_LEVEL = 3;
const NONE = 'none';

type CustomDisplayPropertiesData = {
  key: string;
  value: CssCustomProperties[string];
};

type StylesNamespace = 'cssProperties' | 'cssCustomProperties';

const isRootEditorPointer = (
  editorPointer: EditorPointer<CompRef>,
  editorPointerAPI: EditorPointerServiceAPI,
) => {
  const innerPath = editorPointerAPI.getInnerElementPath(editorPointer) ?? [];
  return innerPath.length <= 1;
};

const isValidDisplayPropertyItem = (
  item: CssPropertyItem | CssCustomProperties[string],
) => ((item.display?.displayValues ?? []) as string[])?.includes(NONE);

const getCustomDisplayProperties = (
  cssCustomProperties: CssCustomProperties,
): CustomDisplayPropertiesData[] =>
  Object.entries(cssCustomProperties).flatMap(([key, value]) =>
    isValidDisplayPropertyItem(value) ? [{ key, value }] : [],
  );

const resolveManifestBranch = (
  pointer: EditorPointer<CompRef>,
  componentsAPI: ComponentsService,
  editorPointerAPI: EditorPointerServiceAPI,
  documentServicesAPI: DocumentServicesObject,
): Pick<ElementsPanelProps, 'data' | 'items'> => {
  const data: CheckedStateType = {};

  const resolveBranch = (
    editorPointer: EditorPointer<CompRef>,
    depthLevel = 1,
  ): CheckboxItemType[] => {
    if (depthLevel > MAX_DEPTH_LEVEL) {
      return [];
    }

    const isRootManifest = isRootEditorPointer(editorPointer, editorPointerAPI);
    const compRef = editorPointerAPI.getDocumentPointer(editorPointer);
    const dmStylesByEditorPointer = isRootManifest
      ? documentServicesAPI.components.cssStyle.get(compRef)
      : documentServicesAPI.components.innerElements.cssStyle.get(
          compRef,
          editorPointerAPI.getInnerElementPath(editorPointer) ?? [],
        );

    const ctx = componentsAPI.getContext(editorPointer);
    const { cssCustomProperties, cssProperties } =
      ctx.resolver.getStyleDefinitions();
    const innerElements = componentsAPI.getChildren(editorPointer, false);
    const currentLevelDisplayName = ctx.getDisplayName();
    const isRemovableValue = componentsAPI.behaviors.getValue(
      editorPointer,
      'removable',
    );
    const isElementRemovable = isRemovableValue ?? true;

    const setPropertyValue = (
      key: string,
      namespace: StylesNamespace,
      id: string,
    ) => {
      const defaultValue = componentsAPI.get.defaultStyleValue(
        editorPointer,
        key,
      );

      const dmValue = (dmStylesByEditorPointer as BuilderStyle)?.[namespace]?.[
        key
      ];
      const valueToCompare = dmValue === undefined ? defaultValue : dmValue;
      const isRemoved = valueToCompare === NONE;

      data[id] = !isRemoved;
    };

    const createId = (key: string, namespace: StylesNamespace) => {
      const innerElementPath =
        editorPointerAPI.getInnerElementPath(editorPointer);

      const id = [...(innerElementPath ?? []), namespace, key].join('.');

      setPropertyValue(key, namespace, id);

      return id;
    };

    const manfiestItemDisplayProperty =
      isRootManifest || !isElementRemovable
        ? // we don't need to check root manifest css properties
          undefined
        : Object.entries(cssProperties).find(([, value]) =>
            isValidDisplayPropertyItem(value),
          );

    const customDisplayProperties =
      getCustomDisplayProperties(cssCustomProperties);

    const hasDisplayPropeties =
      !!manfiestItemDisplayProperty?.length && !!customDisplayProperties.length;

    const children = innerElements.flatMap((childEditorPointer) =>
      resolveBranch(
        childEditorPointer,
        hasDisplayPropeties ? depthLevel + 1 : depthLevel,
      ),
    );

    const result: CheckboxItemType[] = customDisplayProperties.map(
      ({ key, value }) => ({
        displayName: value.displayName ?? key,
        id: createId(key, 'cssCustomProperties'),
        tooltipData: '',
        isRemovable: true,
      }),
    );

    if (manfiestItemDisplayProperty) {
      result.push({
        displayName: currentLevelDisplayName ?? '',
        id: createId(manfiestItemDisplayProperty[0], 'cssProperties'),
        tooltipData: '',
        isRemovable: true,
        children,
      });
    }

    if (isRootManifest) {
      return [...result, ...children];
    }

    if (!manfiestItemDisplayProperty) {
      if (depthLevel <= 2 && children.length > 1) {
        return [
          ...result,
          {
            displayName: currentLevelDisplayName ?? '',
            id: createId(currentLevelDisplayName ?? '', 'cssProperties'),
            isRemovable: false,
            children,
          },
        ];
      }

      if (result.length === 0 || children.length === 1) {
        return [...result, ...children];
      }
    }

    return result;
  };

  const preparedItems = resolveBranch(pointer);

  return {
    items:
      preparedItems.length === 1 && !preparedItems[0].isRemovable
        ? preparedItems[0].children ?? []
        : preparedItems,
    data,
  };
};

export const getBuilderComponentElementsPanelProps: ResolverByExtensionType<
  ManifestComponentExtensionType.BUILDER
> = (
  { manifest },
  {
    editorPointerAPI,
    documentServicesAPI,
    editorPointer: _editorPointer,
    editorSelectionHandlers,
    componentsAPI,
  },
) => {
  if (!editorPointerAPI || !manifest) {
    return { items: [] };
  }
  const editorPointer = editorPointerAPI.buildEditorPointer(
    editorPointerAPI.getDocumentPointer(_editorPointer),
    ['root', ...(editorPointerAPI.getInnerElementPath(_editorPointer) ?? [])],
  );

  const compRef = editorPointerAPI.getDocumentPointer(editorPointer);
  const compType = documentServicesAPI.components.getType(compRef);

  if (!compType) {
    return { items: [] };
  }

  const { data, items } = resolveManifestBranch(
    editorPointer,
    componentsAPI,
    editorPointerAPI,
    documentServicesAPI,
  );

  const handleHighlight =
    (handler: EditorHandlers[keyof EditorHandlers]) => () => {
      // const innerPath = getInnerElementsPath(identifier);
      const pointer = editorPointerAPI.buildEditorPointer(compRef, []);

      pointer && handler(pointer);
    };

  const handleToggleState = async (id: string, enable: boolean) => {
    if (id.length < 2) {
      throw new Error('Wrong toggle state identifier');
    }

    const splitId = id.split('.');
    const [namespace, key] = splitId.slice(-2);
    const innerPath = splitId.slice(0, -2);

    const newEditorPointer = editorPointerAPI.buildEditorPointer(
      compRef,
      innerPath,
    );

    const defaultValue =
      namespace === 'cssProperties'
        ? componentsAPI.get.defaultStyleValue(newEditorPointer, key)
        : componentsAPI.get.defaultCustomStyleValue(newEditorPointer, key);

    const valueToSet = enable ? defaultValue ?? 'block' : 'none';

    const styles = {
      [namespace]: {
        [key]: valueToSet,
      },
    };

    if (isRootEditorPointer(newEditorPointer, editorPointerAPI)) {
      documentServicesAPI.components.cssStyle.update(compRef, styles);
    } else {
      documentServicesAPI.components.innerElements.cssStyle.update(
        compRef,
        editorPointerAPI.getInnerElementPath(newEditorPointer) ?? [],
        styles,
      );
    }

    return Promise.resolve({
      [id]: enable,
    });
  };

  return {
    data,
    items,

    onToggleState: handleToggleState,
    highlightComponent: handleHighlight(
      editorSelectionHandlers.highlightComponent,
    ),
    removeHighlight: handleHighlight(editorSelectionHandlers.removeHighlight),
  };
};
